// THREADS projector HOLOGRAM — a floating garment that slowly spins (a "turntable" cycling the 8
// directional renders) and gently bobs, rendered as a cyan, additively-blended sprite so it reads as a
// futuristic projection. Camera-independent (the spin IS the animation), so it only needs `tick(dt)`.

import * as THREE from "three";
import { assetUrl } from "../assetVer.js";

const SCENE = "/assets/scene";
// PixelLab 8-direction order (matches HOG_DIRS / pack-mob.py); we cycle through these as the turntable.
const DIRS = ["south", "south-east", "east", "north-east", "north", "north-west", "west", "south-west"] as const;
const SPIN_MS = 650;   // per rotation frame → a full turn ≈ 5.2s
const BOB_MS = 2000;   // bob period
const BOB = 0.13;      // bob amplitude (world units)

export class ThreadsHologram {
  private readonly sprite: THREE.Sprite;
  private readonly mat: THREE.SpriteMaterial;
  private readonly textures: THREE.Texture[] = [];
  private phase = 0;
  private spinClock = 0;
  private bobClock = 0;

  constructor(group: THREE.Group, x: number, z: number, private readonly baseY = 2.1, scale = 1.6) {
    const loader = new THREE.TextureLoader();
    for (const dir of DIRS) {
      const t = loader.load(assetUrl(`${SCENE}/village/threads/holo_${dir}.png`));
      t.magFilter = t.minFilter = THREE.NearestFilter;
      t.colorSpace = THREE.SRGBColorSpace;
      this.textures.push(t);
    }
    this.mat = new THREE.SpriteMaterial({
      map: this.textures[0], transparent: true, color: 0x7fe9ff,
      blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0.9,
    });
    this.sprite = new THREE.Sprite(this.mat);
    this.sprite.center.set(0.5, 0.5);
    this.sprite.scale.set(scale, scale, 1);
    this.sprite.position.set(x, baseY, z);
    this.sprite.renderOrder = 5; // draw after opaque scene so the additive glow shows
    group.add(this.sprite);
  }

  /** Advance the turntable spin + bob. `dt` in seconds. */
  tick(dt: number): void {
    this.spinClock += dt * 1000;
    while (this.spinClock >= SPIN_MS) {
      this.spinClock -= SPIN_MS;
      this.phase = (this.phase + 1) % this.textures.length;
      this.mat.map = this.textures[this.phase];
      this.mat.needsUpdate = true;
    }
    this.bobClock = (this.bobClock + dt * 1000) % BOB_MS;
    this.sprite.position.y = this.baseY + Math.sin((this.bobClock / BOB_MS) * Math.PI * 2) * BOB;
  }

  dispose(): void {
    this.sprite.parent?.remove(this.sprite);
    for (const t of this.textures) t.dispose();
    this.mat.dispose();
  }
}
